import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { tenantApi, TenantAdmin, Tenant, LoginRequest, isAuthenticated } from '../lib/tenant-api';
import { fetchRolePermissions, clearRolePermissions } from '../lib/permissions';
import { setCurrentAdminRole } from '../lib/api-client';
import { isPublicAuthPath } from '../lib/subdomain';

interface TenantAuthContextType {
  admin: TenantAdmin | null;
  tenant: Tenant | null;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  clearAllTenantData: () => void;
  updateTenant: (tenant: Tenant) => void;
  permissionsError: boolean;
  retryPermissions: () => Promise<void>;
}

const TenantAuthContext = createContext<TenantAuthContextType | undefined>(undefined);

type ProductFlags = Pick<Tenant, 'client_admin_enabled' | 'domain_expert_enabled'>;

const resolveProductFlags = (
  primary: ProductFlags | null | undefined,
  fallback?: ProductFlags | null,
): Required<ProductFlags> => ({
  client_admin_enabled: primary?.client_admin_enabled ?? fallback?.client_admin_enabled ?? true,
  domain_expert_enabled: primary?.domain_expert_enabled ?? fallback?.domain_expert_enabled ?? false,
});

interface TenantAuthProviderProps {
  children: ReactNode;
}

export const TenantAuthProvider: React.FC<TenantAuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<TenantAdmin | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState(false);

  // Function to clear all tenant-related data
  const clearAllTenantData = () => {
    // Clear localStorage items
    localStorage.removeItem('tenant_auth_token');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('tenant_info');
    localStorage.removeItem('widget_preview_api_key');
    if (window.LAWMAai && typeof window.LAWMAai.clearChat === 'function') {
      window.LAWMAai.clearChat();
    }

    // Clear any other possible cached data
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('tenant_') || key.includes('cache_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Clear sessionStorage as well
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith('tenant_') || key.includes('cache_'))) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));

    // Clear cached role permissions
    clearRolePermissions();

    // Clear admin role from api-client interceptor
    setCurrentAdminRole(null);

    // Clear React state
    setAdmin(null);
    setTenant(null);
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated()) {
        try {
          const currentAdmin = await tenantApi.getCurrentAdmin();
          setAdmin(currentAdmin);
          setCurrentAdminRole(currentAdmin.role ?? null);

          // Always fetch fresh tenant settings to ensure we have the latest data
          try {
            const tenantSettings = await tenantApi.getTenantSettings();

            // Get tenant info from stored login response as base
            const storedTenant = localStorage.getItem('tenant_info');
            let baseTenant: Partial<Tenant> = {};

            if (storedTenant) {
              baseTenant = JSON.parse(storedTenant);
            }

            // Create enriched tenant with fresh API data taking priority
            const enrichedTenant = {
              ...baseTenant,
              tenant_type: tenantSettings.tenant_type?.toLowerCase() === 'service' ? 'service' : 'product',
              goal: tenantSettings.goal,
              aggressiveness: tenantSettings.aggressiveness,
              ...resolveProductFlags(tenantSettings, baseTenant),
            } as Tenant;

            // Update localStorage with fresh data
            localStorage.setItem('tenant_info', JSON.stringify(enrichedTenant));
            setTenant(enrichedTenant);
          } catch (settingsError) {
            console.error('Failed to get tenant settings:', settingsError);
            // Fallback to stored tenant if API fails
            const storedTenant = localStorage.getItem('tenant_info');
            if (storedTenant) {
              setTenant(JSON.parse(storedTenant));
            }
          }

          // Fetch role permissions before UI renders
          const permSuccess = await fetchRolePermissions();
          setPermissionsError(!permSuccess);
        } catch (error) {
          console.error('Failed to get current admin:', error);
          // CRITICAL: Clear ALL tenant data on auth failure (no fallbacks)
          clearAllTenantData();

          if (!isPublicAuthPath()) {
            window.location.href = '/login';
          }
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    setIsLoading(true);
    try {
      // CRITICAL: Clear all previous tenant data before new login
      clearAllTenantData();

      const response = await tenantApi.login(credentials);
      setAdmin(response.admin);
      setCurrentAdminRole(response.admin.role ?? null);

      // Immediately set tenant from login response to avoid race conditions
      const loginTenant = {
        ...response.tenant,
        tenant_type: response.tenant.tenant_type?.toLowerCase() === 'service' ? 'service' : 'product',
        ...resolveProductFlags(response.tenant),
      } as Tenant;

      setTenant(loginTenant);
      localStorage.setItem('tenant_info', JSON.stringify(loginTenant));

      // Fetch fresh tenant settings in background to update any other fields
      try {
        const tenantSettings = await tenantApi.getTenantSettings();
        const enrichedTenant = {
          ...loginTenant,
          tenant_type: tenantSettings.tenant_type?.toLowerCase() === 'service' ? 'service' : 'product',
          goal: tenantSettings.goal,
          aggressiveness: tenantSettings.aggressiveness,
          ...resolveProductFlags(tenantSettings, loginTenant),
        } as Tenant;

        // Always update with fresh settings data - this is the authoritative source
        setTenant(enrichedTenant);
        localStorage.setItem('tenant_info', JSON.stringify(enrichedTenant));
      } catch (settingsError) {
        console.warn('Failed to fetch fresh tenant settings after login:', settingsError);
        // Continue with login tenant data - don't fail the login
      }

      // Fetch role permissions before UI renders
      const permSuccess = await fetchRolePermissions();
      setPermissionsError(!permSuccess);

      // Signal that login is fully complete so WidgetAutoLoader can initialise.
      // setTimeout(0) defers the event until after React has re-rendered and run
      // the new effects — ensuring the listener closure sees isAuthenticated=true.
      setTimeout(() => window.dispatchEvent(new CustomEvent('user-logged-in')), 0);

    } catch (error) {
      console.error('Login failed:', error);
      // Ensure no cached data remains on failed login
      clearAllTenantData();
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear all data via API logout
    tenantApi.logout();

    // Clear all tenant data comprehensively
    clearAllTenantData();

    // Full page reload to flush all in-memory state (React Query cache, globals, etc.)
    window.location.href = '/login';
  };

  const updateTenant = (updatedTenant: Tenant) => {
    setTenant(updatedTenant);
    // Update localStorage as well
    localStorage.setItem('tenant_info', JSON.stringify(updatedTenant));
  };

  const retryPermissions = async () => {
    const success = await fetchRolePermissions();
    setPermissionsError(!success);
  };

  const value: TenantAuthContextType = {
    admin,
    tenant,
    isLoading,
    login,
    logout,
    isAuthenticated: !!admin,
    clearAllTenantData,
    updateTenant,
    permissionsError,
    retryPermissions,
  };

  return (
    <TenantAuthContext.Provider value={value}>
      {children}
    </TenantAuthContext.Provider>
  );
};

export const useTenantAuth = (): TenantAuthContextType => {
  const context = useContext(TenantAuthContext);
  if (context === undefined) {
    throw new Error('useTenantAuth must be used within a TenantAuthProvider');
  }
  return context;
};
